import { ApiQueryName } from "@/models/api"
import { User } from "@/models/user"
import { useRenameQueryResult } from "@/services/utils"
import { useQueryKey } from "@/services/utils/useQueryKey"
import { HttpError, toHttpError } from "@/utils/api"
import { resolveQueryThrowOnError } from "@/utils/queryClient"
import { useQuery } from "@tanstack/react-query"
import type { UseQueryOptions } from "@tanstack/react-query"
import type { RecordProxy } from "spraypaint/lib-esm/proxies"

export const isUnavailableUserError = (error: unknown): boolean =>
  error instanceof HttpError && (error.status === 403 || error.status === 404)

const getUserById = async (userId: string): Promise<User> => {
  try {
    const response: RecordProxy<User> = await User.find(userId)

    return response.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

interface UseUserOptions {
  queryOptions?: Omit<UseQueryOptions<User, HttpError>, "queryFn" | "queryKey">
  userId: string | null
}

/**
 * Returns a user by id (plural `/api/v1/users/:id`).
 * Do not use for auth "me" — that stays on singular `/api/v1/user`.
 */
export const useUser = ({ userId, queryOptions = {} }: UseUserOptions) => {
  const queryKey = useQueryKey(ApiQueryName.User, "getOne", userId)
  const {
    enabled,
    retry = (failureCount, error) => {
      if (isUnavailableUserError(error)) {
        return false
      }

      return failureCount < 3
    },
    throwOnError = resolveQueryThrowOnError(
      (error) => !isUnavailableUserError(error)
    ),
    ...restQueryOptions
  } = queryOptions

  const queryResult = useQuery<User, HttpError>({
    ...restQueryOptions,
    enabled: userId !== null && (enabled ?? true),
    queryFn: async () => {
      if (userId === null) {
        throw new Error("userId is required to fetch user details")
      }

      return await getUserById(userId)
    },
    queryKey,
    retry,
    throwOnError,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.User)
}
